<script setup lang="ts">
import { storeToRefs } from "pinia";
import { type Followable } from "~/types";

const followStore = useFollowingStore();
const { getFollowingOpened, followingTab, getFollowStatus } =
  storeToRefs(followStore);
const activeTab = ref<string>("following");

const { getFollowers, getFollowings } = storeToRefs(followStore);

const filterFollowing = ref("");
const filteredFollowing = computed(() => {
  let filter = filterFollowing.value;

  if (!filter.length) return getFollowings.value.data;

  return getFollowings.value.data.filter((item: any) => {
    if (item.followable)
      return item.followable.name.toLowerCase().includes(filter.toLowerCase());
    else return item.user.name.toLowerCase().includes(filter.toLowerCase());
  });
});

const filterFollowers = ref("");
const filteredFollowers = computed(() => {
  let filter = filterFollowers.value;

  if (!filter.length) return getFollowers.value.data;

  return getFollowers.value.data.filter((item: any) => {
    if (item.followable)
      return item.followable.name.toLowerCase().includes(filter.toLowerCase());
    else return item.user.name.toLowerCase().includes(filter.toLowerCase());
  });
});

const close = () => {
  followStore.closeModal();
};

watch(followingTab, async () => {
  switch (followingTab.value) {
    case "following":
      if (getFollowings.value.data.length <= 0)
        await followStore.fetchFollowingList({ per_page: 50 });
      break;
    case "followers":
      if (getFollowers.value.data.length <= 0)
        await followStore.fetchFollowersList({ per_page: 50 });
      break;
    default:
      break;
  }
});

const unfollow = async (hash: string) => {
  try {
    let data = followStore.unfollow(hash);
    await followStore.fetchFollowingList({ per_page: 50 });
    await followStore.fetchFollowersList({ per_page: 50 });
  } catch (error) {
  }
};
</script>

<template>
  <div class="overlay">
    <div class="overlay__backdrop" @click="close()"></div>
    <div class="modal-follow">
      <div class="loading" v-if="getFollowStatus === 'pending'">
        <DsLoader />
      </div>

      <div class="modal-follow__tabs mb-3">
        <a
          @click.prevent="followingTab = 'following'"
          :class="{ active: followingTab === 'following' }"
          href="#"
          >Seguindo</a
        >
        <a
          @click.prevent="followingTab = 'followers'"
          :class="{ active: followingTab === 'followers' }"
          href="#"
          >Seguidores</a
        >
      </div>

      <div class="search-box">
        <div class="bar">
          <input type="text" v-model="filterFollowing" placeholder="Search" />

          <span class="input-border" aria-hidden="true"></span>

          <button type="button">
            <MGIcon icon="search" />
          </button>
        </div>

        <button class="btn btn-link" type="button">
          <MGIcon icon="filter" size="2x" />
        </button>
      </div>

      <div class="scroll-container">
        <transition name="fade" :duration="100">
          <div class="follow-tab" v-if="followingTab === 'following'">
            <div
              v-for="following in filteredFollowing"
              :key="following.id"
              class="follow-tab__card"
            >
              <div
                class="follow-tab__card__container"
                v-if="following?.followable"
              >
                <div class="follow-tab__card__container--content">
                  <div class="follow-tab__card__container--img">
                    <img
                      :src="
                        following.followable.url_cover ||
                        following.followable.logo_url
                      "
                      class=""
                    />
                  </div>
                  <div class="follow-tab__card__container--name">
                    {{ following.followable.name }}
                  </div>
                </div>
                <div>
                  <div class="follow-tab__card__actions">
                    <div
                      class="btn secondary"
                      @click.prevent="
                        unfollow(following.followable.follow_hash)
                      "
                    >
                      Remover
                    </div>
                  </div>
                </div>
              </div>

              <div class="follow-tab__card__container" v-else>
                <div class="follow-tab__card__container--content">
                  <div class="follow-tab__card__container--img">
                    <img :src="following.user?.avatar_url" class="" />
                  </div>
                  <div class="follow-tab__card__container--name">
                    {{ following.user?.name }}
                  </div>
                </div>
                <div>
                  <div class="follow-tab__card__actions">
                    <div
                      class="btn secondary"
                      @click.prevent="
                        unfollow(following.followable.follow_hash)
                      "
                    >
                      Remover
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
        <transition name="fade" :duration="100">
          <div class="follow-tab" v-if="followingTab === 'followers'">
            <div
              v-for="follower in filteredFollowers"
              :key="follower.id"
              class="follow-tab__card"
            >
              <div
                class="follow-tab__card__container"
                v-if="follower?.followable"
              >
                <div class="follow-tab__card__container--content">
                  <div class="follow-tab__card__container--img">
                    <img
                      :src="
                        follower.followable.url_cover ||
                        follower.followable.logo_url
                      "
                      class=""
                    />
                  </div>
                  <div class="follow-tab__card__container--name">
                    {{ follower.followable.name }}
                  </div>
                </div>
                <div>
                  <div class="follow-tab__card__actions">
                    <div class="btn btn-link me-1">Seguir</div>
                    <div
                      class="btn secondary"
                      @click.prevent="unfollow(follower.followable.follow_hash)"
                    >
                      Remover
                    </div>
                  </div>
                </div>
              </div>

              <div class="follow-tab__card__container" v-else>
                <div class="follow-tab__card__container--content">
                  <div class="follow-tab__card__container--img">
                    <img :src="follower.user.avatar_url" class="" />
                  </div>
                  <div class="follow-tab__card__container--name">
                    {{ follower.user.name }}
                  </div>
                </div>
                <div>
                  <div class="follow-tab__card__actions">
                    <div class="btn btn-link me-1">Seguir</div>
                    <div
                      class="btn secondary"
                      @click.prevent="unfollow(follower.followable.follow_hash)"
                    >
                      Remover
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.overlay {
  position: fixed;
  height: 100vh;
  width: 100vw;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  &__backdrop {
    position: absolute;
    height: 100vh;
    width: 100vw;
    z-index: 1;
  }
  .modal-follow {
    position: relative;
    z-index: 2;
    width: 480px;
    height: 592px;
    display: block;
    background: var(--card-article-bg);
    padding-top: 1.5rem;

    .loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 999;
      background-color: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__tabs {
      display: flex;

      a {
        font-size: 0.75rem;
        text-decoration: none;
        color: var(--active);
        border-bottom: 1px solid var(--sidebar-toggler-fg);
        padding-bottom: 0.75rem;
        flex: 1;
        text-align: center;

        &.active {
          border-bottom: 2px solid var(--section-title-marker-fg);
        }
      }
    }
  }

  .follow-tab {
    &__card {
      display: flex;
      flex-wrap: nowrap;
      flex-direction: row;
      justify-content: flex-start;
      border-bottom: 1px solid var(--card-article-border);
      padding: 0.75rem 0;
      &__container {
        display: flex;
        padding: 0 1rem;
        align-content: center;
        align-items: center;
        flex-wrap: wrap;
        flex: 1;
        justify-content: space-between;
        &--img {
          height: 48px;
          width: 48px;
          border-radius: 100%;
          margin-right: 1rem;
          align-self: center;
          overflow: hidden;
          position: relative;
          img {
            position: absolute;
            top: 50%;
            left: 50%;
            max-width: 100%;
            transform: translate(-50%, -50%);
          }
        }

        &--name {
          font-size: 14px;
          color: var(--active);
          width: 14.75rem;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &--content {
          display: flex;
          flex: 1 0 auto;
          align-items: center;
          flex-wrap: wrap;
          &:last-child {
            justify-content: center;
          }
        }
      }
      &__actions {
        display: flex;
      }
    }
    .btn:not(.secondary) {
      color: var(--bt-active);
    }
    .btn {
      width: 72px;
    }
  }

  .search-box {
    position: relative;
    display: flex;
    flex-direction: row;
    margin: 0 2rem 2rem 2rem;

    .bar {
      position: relative;
      display: flex;
      max-width: 100%;
      flex-direction: row-reverse;
      flex: 1 0 auto;
      border-bottom: 1px solid var(--search-bar-border-color);

      .input-border {
        position: absolute;
        display: flex;
        bottom: -2px;
        right: 0;
        left: 0;
        height: 2px;
        width: 0;
        margin: 0 auto;
        background: var(--search-bar-active-border-color);
        transition: 0.2s ease-in-out;
        z-index: 2;
      }

      input {
        width: 100%;
        padding: 0.25rem 1rem;
        border: 0;
        outline: 0;
        background: var(--search-bar-bg);
        font-size: 1rem;
        color: var(--search-bar-fg);

        &:focus + .input-border,
        &:focus-visible + .input-border,
        &:active + .input-border {
          width: 100%;
        }
      }

      button {
        position: relative;
        right: 1px;
        width: 32px;
        height: 32px;
        border: 0;
        outline: 0;
        background: var(--search-bar-bg);
        color: var(--search-bar-fg);
        transition: 0.15s ease-in-out;
        z-index: 1;

        &:hover {
          color: var(--search-bar-active-fg);
        }

        .icon {
          font-size: 1.85rem !important;
        }
      }
    }

    .btn {
      width: 32px;
      height: 32px;
      color: var(--search-bar-fg);
      padding: 0;
      margin-left: 0.25rem;
    }
  }

  .scroll-container {
    height: 457px;
    overflow-y: auto;
    padding-right: 10px;
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-button {
      width: 0px;
      height: 0px;
    }

    &::-webkit-scrollbar-thumb {
      background: #808080;
      border: 15px none #ffffff;
      border-radius: 0px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: var(--bt-active);
    }

    &::-webkit-scrollbar-thumb:active {
      background: #aaaaaa;
    }

    &::-webkit-scrollbar-track {
      background: #272930;
      border: 8px none #ffffff;
      border-radius: 0px;
    }

    &::-webkit-scrollbar-track:hover {
      background: #272930;
    }

    &::-webkit-scrollbar-track:active {
      background: #333333;
    }

    &::-webkit-scrollbar-corner {
      background: transparent;
    }
  }
}
</style>
